import { WidgetsValue } from './model';
import { z } from 'zod';
import { zSchema } from '../util/zod';
import { jObjectBaseSchema, jObjectLittle } from './objectBase';

export const UserLevel = {
    ADMIN: 9,
    USER: 5,
    EXTERNAL: 3,
    GUEST: 0,
} as const;

export const UserVariant = {
    EXTRABOT: 'extraBot',
} as const;

export type jUserLittle = jObjectLittle & {
    level: (typeof UserLevel)[keyof typeof UserLevel];
    dateCreation: string;
    dateModified: string;
    variant?: (typeof UserVariant)[keyof typeof UserVariant] | undefined;
    [key: string]: WidgetsValue | undefined;
};
const jUserLittleSchemaTmp = jObjectBaseSchema.extend({
    title: z.string(),
    _url: z.string(),
    level: z.nativeEnum(UserLevel),
    variant: z.nativeEnum(UserVariant).optional(),
    dateCreation: z.string(),
    dateModified: z.string(),
});
// .catchall(widgetsValueSchema);
export const jUserLittleSchema = zSchema<
    jUserLittle,
    typeof jUserLittleSchemaTmp.shape /* , typeof widgetsValueSchema */
>(jUserLittleSchemaTmp);

export type jUserList = jUserLittle & {
    active: number;
};
const jUserListSchemaTmp = jUserLittleSchema.extend({
    active: z.number(),
});
export const jUserListSchema = zSchema<jUserList, typeof jUserListSchemaTmp.shape>(jUserListSchemaTmp);

export const validateIsFormatUserList = (object: object): object is jUserList => {
    return jUserListSchema.safeParse(object).success;
};

export const jUserViewSchema = jUserListSchema;
export type jUserView = z.infer<typeof jUserViewSchema>;
export const validateIsFormatUserView = (object: object): object is jUserView => {
    return jUserViewSchema.safeParse(object).success;
};

export const userMapModelSchema = z.object({
    idInType: z.number(),
    lat: z.number(),
    lng: z.number(),
});
export type UserMapModel = z.infer<typeof userMapModelSchema>;
export const validateIsFormatUserMapModel = (object: object): object is UserMapModel => {
    return userMapModelSchema.safeParse(object).success;
};

export const userDefaultFieldsTypeSchema = z.object({
    title: z.string(),
    firstname: z.string(),
    lastname: z.string(),
    mail: z.union([z.string(), z.undefined()]).optional(),
    description: z.string().optional(),
    dateOfBirth: z.string().optional(),
    phoneNumber: z.string().optional(),
    language: z.string().optional(),
    timeZone: z.string().optional(),
    mobileNumber: z.string().optional(),
    company: z.string().optional(),
    country: z.string().optional(),
    department: z.string().optional(),
    function: z.string().optional(),
    webSite: z.string().optional(),
    image: z.union([z.string(), z.undefined()]).optional(),
    userBanner: z.union([z.string(), z.undefined()]).optional(),
    field1: z.string().optional(),
    field2: z.string().optional(),
    field3: z.string().optional(),
    field4: z.string().optional(),
    field5: z.string().optional(),
});

export const jUserValuesSchema = userDefaultFieldsTypeSchema.required().extend({
    level: z.string(),
    image: z.string().optional(),
    businessManagementManager: jUserLittleSchema.array().optional(),
    businessNetworkingUserToCompanySrc: jUserLittleSchema.array().optional(),
});
export type jUserValues = z.infer<typeof jUserValuesSchema>;

export const userFieldsConfigurationTypeSchema = z.object({
    user_field_edit_profil: z.record(userDefaultFieldsTypeSchema.keyof(), z.number()),
    user_field_editable: z.record(userDefaultFieldsTypeSchema.keyof(), z.number()),
    user_field_my_page: z.record(userDefaultFieldsTypeSchema.keyof(), z.number()),
    user_page_mandatory_fields: z.object({
        firstname: z.enum(['0', '1']),
        lastname: z.enum(['0', '1']),
        pseudo: z.enum(['0', '1']),
    }),
});
export type UserFieldsConfigurationType = z.infer<typeof userFieldsConfigurationTypeSchema>;

export const userFieldSchema = z.object({
    name: z.string(),
    value: z.union([z.string(), z.record(z.string(), z.any())]),
});
export type UserField = z.infer<typeof userFieldSchema>;

export const userFieldsSchema = z.enum(['popover']); // TODO : à compléter
export type UserFieldsType = z.infer<typeof userFieldsSchema>;

export const userValuesSchema = z.object({
    values: jUserValuesSchema,
    mobileEditableFields: jUserValuesSchema.keyof().array(),
});
export type UserValues = z.infer<typeof userValuesSchema>;

export const businessContactSchema = z.object({
    mail: z.union([z.string(), z.undefined()]),
    title: z.string(),
    firstname: z.string(),
    lastname: z.string(),
    phoneNumber: z.union([z.string(), z.undefined()]).optional(),
    company: z.union([z.string(), z.undefined()]).optional(),
    function: z.union([z.string(), z.undefined()]).optional(),
    uri: z.union([z.string(), z.undefined()]).optional(),
    field1: z.union([z.string(), z.undefined()]).optional(),
    field2: z.union([z.string(), z.undefined()]).optional(),
    field3: z.union([z.string(), z.undefined()]).optional(),
    field4: z.union([z.string(), z.undefined()]).optional(),
    field5: z.union([z.string(), z.undefined()]).optional(),
});
export type BusinessContact = z.infer<typeof businessContactSchema>;

export const userDirectoryConfigurationSchema = z.object({
    userDirectoryShow: z.union([z.literal(''), z.literal('1')]),
    contactDirectoryShow: z.union([z.literal('0'), z.literal('1')]),
    businessContactShow: z.boolean(),
    menu: z.union([z.literal('0'), z.literal('1')]),
    cardFields: z.string().array(),
    tableFields: z.string().array(),
    viewFields: z.string().array(),
    userGeoloc: z.boolean().optional(),
    sort: z.string(),
    cnt: z.number().optional(),
    filters: z
        .object({
            name: z.string(),
            type: z.string(),
        })
        .array(),
});
export type UserDirectoryConfiguration = z.infer<typeof userDirectoryConfigurationSchema>;
export const validateIsFormatUserDirectoryConfiguration = (object: object): object is UserDirectoryConfiguration => {
    return userDirectoryConfigurationSchema.safeParse(object).success;
};

export const FREQUENCY_INHERIT = -1;
export const FREQUENCY_NOW = 0;
export const FREQUENCY_GROUP = 5;
export const FREQUENCY_DAILY = 1;
export const FREQUENCY_WEEKLY = 2;
export const FREQUENCY_NEVER = 10;

export const UserNotificationsConfigurationSchema = z.object({
    default: z.object({
        mailEnabled: z.boolean(),
        mailFrequency: z.union([
            z.literal(FREQUENCY_NOW),
            z.literal(FREQUENCY_DAILY),
            z.literal(FREQUENCY_GROUP),
            z.literal(FREQUENCY_WEEKLY),
            z.literal(FREQUENCY_NEVER),
        ]),
        mailGroupFrequency: z.number(),
        wantMailSpotit: z.boolean(),
        wantMailComment: z.boolean(),
        wantMailSubscriber: z.boolean(),
        wantActivitySpotit: z.boolean(),
        wantActivityComment: z.boolean(),
    }),
    userSpotConfigurations: z.array(
        z.object({
            spotUri: z.string(),
            spotTitle: z.string(),
            mailLocked: z.boolean(),
            wantMailSpotit: z.boolean(),
            wantMailComment: z.boolean(),
            wantMailSubscriber: z.boolean(),
            mailFrequency: z.union([
                z.literal(FREQUENCY_INHERIT),
                z.literal(FREQUENCY_NOW),
                z.literal(FREQUENCY_DAILY),
                z.literal(FREQUENCY_WEEKLY),
                z.literal(FREQUENCY_NEVER),
            ]),
            wantActivitySpotit: z.boolean(),
            wantActivityComment: z.boolean(),
        }),
    ),
    userNetworkConfigurations: z.array(
        z.object({
            networkUri: z.string(),
            networkTitle: z.string(),
            wantMail: z.boolean(),
            wantActivity: z.boolean(),
        }),
    ),
});
export type UserNotificationsConfiguration = z.infer<typeof UserNotificationsConfigurationSchema>;
